import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Building2, LogOut, Menu, X, Anchor } from "lucide-react";
import { Toaster } from "sonner";
import { useAuth } from "../../context/AuthContext";

const PLATFORM_NAV = [
  {
    to: "/platform",
    label: "Tenants",
    icon: Building2,
    end: true,
    testid: "platform-nav-tenants",
  },
];

function NavItems({ onNavigate, variant = "desktop" }) {
  return (
    <ul className="flex flex-col" data-testid={`platform-nav-${variant}`}>
      {PLATFORM_NAV.map(({ to, label, icon: Icon, end, testid }) => (
        <li key={to}>
          <NavLink
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={variant === "mobile" ? `${testid}-mobile` : testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3.5 font-display uppercase text-sm tracking-wide border-l-4 transition-colors ${
                isActive
                  ? "bg-ink text-white border-brand"
                  : "text-ink/70 border-transparent hover:bg-secondary"
              }`
            }
          >
            <Icon size={18} strokeWidth={2.25} />
            {label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function PlatformHeader() {
  return (
    <div
      className="px-6 py-6 border-b border-border"
      data-testid="platform-header"
    >
      <div className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
        <Anchor size={20} className="text-brand" />
        <span>
          Blue<span className="text-brand">Harbor</span>
        </span>
      </div>
      <div className="mt-1 data-sku">
        <span data-testid="platform-header-subtitle">Control Center</span>
      </div>
    </div>
  );
}

function PlatformSidebar({ user, onLogout }) {
  return (
    <aside
      className="hidden md:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-border flex-col z-30"
      data-testid="platform-sidebar"
    >
      <PlatformHeader />
      <nav className="flex-1 py-2 overflow-y-auto">
        <NavItems variant="desktop" />
      </nav>
      <div className="border-t border-border p-4">
        <div className="px-2 pb-3">
          <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            Signed in as
          </div>
          <div
            className="font-display font-bold text-sm truncate"
            data-testid="platform-sidebar-email"
          >
            {user?.email}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand mt-1">
            Platform Operator
          </div>
        </div>
        <button
          onClick={onLogout}
          className="btn-secondary h-11 w-full"
          data-testid="platform-sidebar-logout"
        >
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </aside>
  );
}

function PlatformTopBar({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <div
        className="md:hidden sticky top-0 z-30 bg-white border-b border-border px-4 h-14 flex items-center justify-between"
        data-testid="platform-topbar"
      >
        <button
          onClick={() => setOpen(true)}
          className="h-10 w-10 -ml-2 flex items-center justify-center"
          aria-label="Open menu"
          data-testid="platform-menu-button"
        >
          <Menu size={22} strokeWidth={2.5} />
        </button>
        <div className="font-display font-black text-lg flex items-baseline gap-2">
          <span>
            Blue<span className="text-brand">Harbor</span>
          </span>
          <span className="font-mono text-[11px] font-bold tracking-wider text-brand">
            · OPS
          </span>
        </div>
        <div className="w-10" />
      </div>

      {open && (
        <div
          className="md:hidden fixed inset-0 z-50 flex"
          data-testid="platform-drawer"
        >
          <div
            className="absolute inset-0 bg-ink/60"
            onClick={() => setOpen(false)}
          />
          <aside className="relative bg-white w-80 max-w-[85%] h-full flex flex-col animate-slide-up shadow-xl">
            <div className="px-6 py-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-display font-black text-xl tracking-tight">
                  Blue<span className="text-brand">Harbor</span>
                </div>
                <div className="data-sku">Control Center</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="h-10 w-10 flex items-center justify-center"
                aria-label="Close menu"
                data-testid="platform-menu-close"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 py-2 overflow-y-auto">
              <NavItems variant="mobile" onNavigate={() => setOpen(false)} />
            </nav>
            <div className="border-t border-border p-4">
              <div className="px-2 pb-3">
                <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  Signed in as
                </div>
                <div
                  className="font-display font-bold text-sm truncate"
                  data-testid="platform-drawer-email"
                >
                  {user?.email}
                </div>
              </div>
              <button
                onClick={onLogout}
                className="btn-secondary h-11 w-full"
                data-testid="platform-drawer-logout"
              >
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

export default function PlatformLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    const res = await logout();
    if (!res.ok && res.pending) {
      const ok = window.confirm(
        `You have ${res.pending} unsynced action${res.pending === 1 ? "" : "s"}. Continue?`,
      );
      if (!ok) return;
      await logout({ force: true });
    }
    nav("/login", { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-paper md:pl-64"
      data-testid="platform-layout"
    >
      <PlatformSidebar user={user} onLogout={onLogout} />
      <PlatformTopBar user={user} onLogout={onLogout} />
      <main className="px-6 py-8">
        <Outlet />
      </main>
      <Toaster position="top-right" richColors offset={16} />
    </div>
  );
}
